import { camelCase } from "lodash-es"
import { z } from "zod"

import { isNil } from "./any"

/**
 * Validation error type that maintains compatibility with the previous Yup-based implementation.
 * This allows existing error handling code to work without changes.
 */
export type ValidationError = {
  path: string
  message: string
  type: string
  errors: Array<string>
}

/**
 * Validates an object against a Zod schema and returns an array of validation errors.
 * Returns an empty array if validation passes.
 *
 * @param schema - A Zod schema to validate against
 * @param obj - The object to validate
 * @returns An array of ValidationError objects (empty if valid)
 */
export function validate<T>(schema: z.ZodSchema<T>, obj: unknown): Array<ValidationError> {
  const result = schema.safeParse(obj)

  if (result.success) {
    return []
  }

  return result.error.issues.map((issue) => ({
    // Serialize path as JSON to handle column names that contain dots
    // validate.ts will JSON.parse() this back when needed
    path: JSON.stringify(issue.path),
    message: issue.message,
    type: issue.code,
    errors: [issue.message],
  }))
}

/**
 * Recursively applies `fn` to all keys in a object returning a new object. Warning: This is an expensive function. Avoid
 * using repeatedly, and cache results when possible.
 * @param fn
 * @param obj
 * @returns a new object
 */
export function deepTransformKeys(fn: (key: string, value: unknown) => string, obj: Record<string, unknown>) {
  const newObj: typeof obj = {}

  if (typeof obj !== "object") return obj

  for (const key in obj) {
    const val = obj[key]
    const newKey = fn(key, val)

    if (isNil(val)) {
      newObj[newKey] = val
      continue
    }

    const { constructor } = Object.getPrototypeOf(val)

    newObj[newKey] =
      constructor === Object
        ? deepTransformKeys(fn, val as Record<string, unknown>)
        : constructor === Array
          ? (val as unknown[]).map((x) => deepTransformKeys(fn, x as Record<string, unknown>))
          : val
  }

  return newObj
}

export function camelCaseKeys(obj: Record<string, unknown>) {
  // Ensure that objects prefixed with underscore keep the underscore (e.g. `_my_key` becomes `_myKey`)
  return deepTransformKeys((key) => {
    let prefix = ""
    if (key.startsWith("_")) {
      prefix = "_"
    }
    return prefix + camelCase(key)
  }, obj)
}

export type RecordPredicate<V, K extends PropertyKey> = (val: V, key: K, obj: Record<K, V>) => boolean
export function filterValues<K extends PropertyKey, V>(fn: RecordPredicate<V, K>, obj: Record<K, V>) {
  const newObj = {} as Record<K, V>

  for (const key in obj) {
    if (fn(obj[key], key, obj) === true) newObj[key] = obj[key]
  }

  return newObj
}

export function permitKeys<V, K extends PropertyKey>(obj: Record<K, V>, keys: K[]): Record<K, V> {
  return keys.reduce(
    (acc, key) => {
      acc[key] = obj[key]
      return acc
    },
    {} as Record<K, V>
  )
}

export type RecordValueMapper<V, K extends PropertyKey> = (val: V, key: K, obj: Record<K, V>) => V
export function mapValues<V, K extends PropertyKey>(fn: RecordValueMapper<V, K>, obj: Record<K, V>) {
  const newObj = {} as Record<K, V>

  for (const key in obj) {
    newObj[key] = fn(obj[key], key, obj)
  }

  return newObj
}

export type RecordKeyMapper<V, K extends PropertyKey> = (val: V, key: K, obj: Record<K, V>) => K
export function mapKeys<V, K extends PropertyKey>(fn: RecordKeyMapper<V, K>, obj: Record<K, V>) {
  const newObj = {} as Record<K, V>

  for (const key in obj) {
    newObj[fn(obj[key], key, obj)] = obj[key]
  }

  return newObj
}

export type RecordMapper<V, K extends PropertyKey> = (val: V, key: K, obj: Record<K, V>) => [K, V]

export function mapPairs<V, K extends PropertyKey>(fn: RecordMapper<V, K>, obj: Record<K, V>) {
  const newObj = {} as Record<K, V>

  for (const key in obj) {
    const [newKey, newValue] = fn(obj[key], key, obj)
    newObj[newKey] = newValue
  }

  return newObj
}

export function firstKey<V, K extends PropertyKey>(obj: Record<K, V>): null | K {
  for (const key in obj) {
    return key
  }
  return null
}

export function firstValue<V, K extends PropertyKey>(obj: Record<K, V>): null | V {
  for (const key in obj) {
    return obj[key]
  }
  return null
}

export type RecordEach<V, K extends PropertyKey> = (val: V, key: K, obj: Record<K, V>) => unknown
export function forEachGivenKey<V, K extends PropertyKey>(fn: RecordEach<V, K>, obj: Record<K, V>, keys: K[]) {
  for (const key of keys) {
    fn(obj[key], key, obj)
  }
}
